import {
  Agent,
  AssociatedFile,
  MessageDirection,
  RatingOption,
  SmartResponseSelectOption,
  WebChatEventAgent,
  WebChatParticipant,
} from '@ucc/cc-common-ui';

export interface NotificationSubscriptionBody {
  channelId: string;
  participant?: WebChatParticipant;
}

export enum NotificationEventsType {
  WEB_CHAT_INITIAL_STATE = 'WEB_CHAT_INITIAL_STATE',
  WEB_CHAT_INBOUND_MESSAGE = 'WEB_CHAT_INBOUND_MESSAGE',
  WEB_CHAT_OUTBOUND_MESSAGE = 'WEB_CHAT_OUTBOUND_MESSAGE',
  WEB_CHAT_SMART_RESPONSE_SELECT_QUESTION = 'WEB_CHAT_SMART_RESPONSE_SELECT_QUESTION',
  WEB_CHAT_ENDED = 'WEB_CHAT_ENDED',
  WEB_CHAT_AFTER_HOURS_MESSAGE = 'WEB_CHAT_AFTER_HOURS_MESSAGE',
  AGENT_TYPING_INDICATOR = 'AGENT_TYPING_INDICATOR',
  AGENT_READ_RECEIPT = 'AGENT_READ_RECEIPT',
  WEB_CHAT_SURVEY_RATING_QUESTION = 'WEB_CHAT_SURVEY_RATING_QUESTION',
  WEB_CHAT_SURVEY_FEEDBACK_QUESTION = 'WEB_CHAT_SURVEY_FEEDBACK_QUESTION',
  WEB_CHAT_SURVEY_RATING_RESPONSE = 'WEB_CHAT_SURVEY_RATING_RESPONSE',
  WEB_CHAT_SURVEY_FEEDBACK_RESPONSE = 'WEB_CHAT_SURVEY_FEEDBACK_RESPONSE',
}

export interface NotificationAgent {
  key: string;
  firstName: string;
}

export interface NotificationSubscription {
  callbackURL: string;
  channelData: {
    channelType: string;
    isConnected: boolean;
    channelURL: string;
  };
  channelId: string;
  channelLifetime: number;
  channelNickname: string;
  doNotDisturbAware: boolean;
  resourceURL: string;
}

export interface Participant {
  key: string;
}

type ConversationEventsInitialState =
  | (WebChatInboundMessage & {
      type: NotificationEventsType.WEB_CHAT_INBOUND_MESSAGE;
    })
  | (WebChatOutboundMessage & {
      type: NotificationEventsType.WEB_CHAT_OUTBOUND_MESSAGE;
    })
  | (WebChatAfterHoursMessageEvent & {
      type: NotificationEventsType.WEB_CHAT_AFTER_HOURS_MESSAGE;
    })
  | (WebChatSmartResponseSelectQuestionMessageEvent & {
      type: NotificationEventsType.WEB_CHAT_SMART_RESPONSE_SELECT_QUESTION;
    });

export interface WebChatInitialState {
  timestamp: string;
  content: WebChatInitialStateContent;
}

export interface WebChatInitialStateContent {
  eventIdentifier: string;
  participant: Participant;
  conversationEvents: ConversationEventsInitialState[];
  agentReadReceipts?: AgentReadReceipt[];
}

export interface AgentReadReceipt {
  agent: WebChatEventAgent;
  readAt: string;
}

export type WebChatMessage = WebChatInboundMessage | WebChatOutboundMessage;

export interface WebChatMessageContent {
  key: string;
  direction: MessageDirection;
  agent?: Agent;
  sentAt: string;
  body: string;
  associatedFiles: AssociatedFile[];
}

export interface WebChatInboundMessage extends WebChatEvent {
  correlationId?: string;
  message: WebChatMessageContent;
}

export interface WebChatOutboundMessage extends WebChatEvent {
  message: WebChatMessageContent;
}

export interface WebChatEnded extends WebChatEvent {
  agent?: {
    firstName: string;
  };
}

export interface WebChatSmartResponseSelectQuestionMessageEvent extends WebChatEvent {
  options: SmartResponseSelectOption[];
}

export interface WebChatEvent {
  eventIdentifier: string;
  participant: Participant;
  sentAt: string;
  type: string;
}

export interface WebChatAfterHoursMessageEvent extends WebChatEvent {
  afterHoursMessage: string;
}

export interface WebChatSurveyInitialized extends WebChatEvent {
  question: string;
  positiveAnswer: RatingOption;
  negativeAnswer: RatingOption;
}

export interface WebChatSurveyFeedbackQuestion extends WebChatEvent {
  feedbackQuestion: string;
}

enum Rating {
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
}

export interface WebChatSurveyRatingResponse extends WebChatEvent {
  rating: Rating;
  content: string;
}

export interface WebChatSurveyFeedbackResponse extends WebChatEvent {
  response: string;
}

export interface WebChatSmartResponseSelectQuestion extends WebChatEvent {
  options: SmartResponseSelectOption[];
}

export interface ChannelRequest {
  command: string;
  timeout: number;

  resolve(value): void;

  reject(reason): void;
}
